/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source'
		},
		classes: {
			videoLoaded: 'mm-video-loaded'
		},
		breakpoints: {
			mobile: 768
		},
		timing: {
			resizeDebounce: 150
		}
	},

	/**
	 * Initialize lazy video loading
	 */
	init() {
		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		const loadVideo = (video) => {
			if (video.dataset.loaded !== 'true') {
				const source = video.querySelector(this.config.selectors.videoSource);
				if (!source?.dataset.src) return;

				source.src = source.dataset.src;
				video.preload = 'metadata';
				video.dataset.loaded = 'true';

				video.load();
				if (video.hasAttribute('autoplay')) {
					video.play().catch((err) => 
						window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message })
					);
				}
			}
		};

		const unloadVideo = (video) => {
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.matchMedia(`(max-width: ${this.config.breakpoints.mobile}px)`).matches;
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) || 
								(isDesktop && !isMobileView) || 
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling
		const handleResize = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', handleResize);

	}
};

/*********************************************************************************************
 * 🎯 GLOBAL MEDIA MANAGER
 */
const GlobalMediaManager = {
	/**
	 * Initialize video management
	 */
	init() {
		VideoManager.init();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;
window.GlobalMediaManager = GlobalMediaManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	GlobalMediaManager.init();
});